import filter from 'lodash/filter'
import React, { useContext, useMemo, useRef, useState, VFC } from 'react'

import { DiscountChip } from '@Components/Chips/DiscountChip'
import { FreeDeliveryChip } from '@Components/Chips/FreeDeliveryChip'
import { NewChip } from '@Components/Chips/NewChip'
import { TwoForOneChip } from '@Components/Chips/TwoForOneChip'
import { BrandLogo } from '@Components/Product/BrandLogo'
import { ProductContext } from '@Components/Product/Context'
import { Info } from '@Components/Product/Display/Info'
import {
  BrandAndReviewWrapper,
  GalleryWrapper,
  MobileOnlyWrapper,
  PDPInfoWrapper,
  PDPTopWrapper,
  ProductChips,
  ReviewLinkButton,
  StyledStarRating,
  WishListContainer,
} from '@Components/Product/Display/styledElements'
import { GallerySlider } from '@Components/Slider/GallerySlider'
import { AddToWishList } from '@Components/WishList/addToWishList'
import { IWithMoney, Product } from '@Generated/graphql'
import { convertToGTM, convertToMParticle, getBaseAttribute } from '@Globals/CMS/ProductHelpers'
import { MetadataContext } from '@Globals/Metadata'
import { useScrollTo } from '@Globals/useScrollTo'

export const DisplayV1: VFC = () => {
  const product = useContext(ProductContext)
  const { store } = useContext(MetadataContext)
  const { value: freeDeliveryThreshold } = store?.config?.freeShippingThreshold as IWithMoney || 0
  // Extract assets for later use
  const assets = useMemo(() => {
    const unsortedAssets = filter(product?.assets, asset => asset.tags?.includes('gallery')) as Product['assets']
    const videoIndex = unsortedAssets?.findIndex(asset => asset.tags?.includes('video'))
    if (videoIndex && videoIndex > -1 && unsortedAssets) {
      const video = unsortedAssets[videoIndex]
      unsortedAssets.splice(videoIndex, 1)
      unsortedAssets.splice(2, 0, video)
    }
    return unsortedAssets
  }, [product])

  const reviewWrapperRef = useRef<HTMLDivElement | null>(null)
  const scrollToReviewBlock = useScrollTo(reviewWrapperRef)

  const { price } = product?.masterVariant || {}

  // Some calculations needed for the "buy two for almost-one" offer
  const freeDelivery = freeDeliveryThreshold && price?.value && (freeDeliveryThreshold < price.value)
  const [galleryIsFullScreen, setGalleryIsFullScreen] = useState(false)

  // TODO: This has to work better.
  if (!product) {
    return <>404</>
  }

  const isNew = getBaseAttribute<boolean>(product?.masterVariant?.attributes || [], 'isNew') || false

  const showChips = isNew || (price?.discount?.percent || price?.discount?.code?.context !== '') || freeDelivery

  return (
    <PDPTopWrapper>
      <GalleryWrapper isFullScreen={galleryIsFullScreen}>
        {assets && assets.length > 0
          ? (
            <GallerySlider
              assets={assets}
              onFullScreenToggle={setGalleryIsFullScreen}
              title={product.name || ''}
            />
          )
          : '(no images found)'}
        {showChips && (
          <ProductChips>
            {product.type === 'glasses' && <TwoForOneChip />}
            {price?.discount && (
              <DiscountChip
                discount={{
                  percentage: price.discount.percent || 0,
                  context: price.discount.code?.context || '',
                }}
                size="small"
              />
            )}
            {isNew && <NewChip size="small" />}
            {freeDelivery && <FreeDeliveryChip size="small" />}
          </ProductChips>
        )}
        <WishListContainer>
          <AddToWishList gtmProduct={convertToGTM(product)} id={product.id} mParticleProduct={convertToMParticle(product, 1)} />
        </WishListContainer>
      </GalleryWrapper>
      <PDPInfoWrapper>
        {product.brand && (
          <BrandAndReviewWrapper>
            {product.brand && <BrandLogo brand={product.brand} />}
            {(product.productReviews?.averageRating || 0) > 0 && (
              <MobileOnlyWrapper>
                <ReviewLinkButton onClick={scrollToReviewBlock} type="button">
                  <StyledStarRating rating={product.productReviews?.averageRating || 0} />
                </ReviewLinkButton>
              </MobileOnlyWrapper>
            )}
          </BrandAndReviewWrapper>
        )}
        <Info reviewWrapperRef={reviewWrapperRef} />
      </PDPInfoWrapper>
    </PDPTopWrapper>
  )
}
